import { Link } from "react-router-dom";
import { ArrowRight, LayoutGrid, MousePointerClick } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import TitleBand from "../../components/TitleBand/TitleBand";
import CtaBand from "../../components/Home/CtaBand";
import ContactForm from "../../components/Forms/ContactForm";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import { useInView } from "../../util/useInView";
import { PageSeo } from "../../seo/Seo";
import SolutionsWheel from "./SolutionsWheel";
import { SOLUTION_GROUPS, groupPath, itemPath, useSolutionTitles } from "./content";
import "./solucao.css";
import "./catalogo.css";

// /solucoes — catálogo completo: o mapa em círculo (os tópicos no anel; parar o mouse num deles abre o informativo
// com as soluções). No celular, onde o círculo não cabe, fica a lista de todas as soluções por tópico. Links antigos
// /solucoes#coreinfra abrem o informativo do tópico (no computador) ou levam ao bloco dele na lista (no celular).
export default function SolutionsCatalogPage() {
  const { t } = useTranslation();
  const { groupTitle, itemTitle } = useSolutionTitles();
  const [ref, state] = useInView<HTMLUListElement>(0.05);

  return (
    <div className="cz-page cz-scope cz-sol-page">
      <PageSeo page="solucoes" path="/solucoes" />
      <Header />
      <main>
        <TitleBand
          title={
            <>
              {t("solutions_catalog.title_lead", "Catálogo de")}{" "}
              <span className="cz-catalog-accent">{t("solutions_catalog.title_accent", "soluções")}</span>
            </>
          }
          icon={LayoutGrid}
          singleLine
        />

        <section className="cz-catalog-map" aria-label={t("solutions_catalog.map_label", "Mapa das soluções")}>
          <p className="cz-catalog-map__hint">
            <MousePointerClick size={16} aria-hidden="true" />
            {t("solutions_catalog.hint", "Clique para explorar cada solução")}
          </p>
          <SolutionsWheel />
        </section>

        <section className="cz-sol-band cz-catalog-list-section" aria-labelledby="cz-catalog-list-title">
          <div className="cz-sol-section">
            <div className="cz-sol-head">
              <span className="cz-eyebrow">{t("solutions_catalog.list_eyebrow", "Catálogo completo")}</span>
              <h2 className="cz-sol-h2" id="cz-catalog-list-title">
                {t("solutions_catalog.list_title", "Todas as soluções")}
              </h2>
            </div>
            <ul ref={ref} className={`cz-catalog-list${state}`}>
              {SOLUTION_GROUPS.map((group, i) => {
                const Icon = group.icon;
                return (
                  <li key={group.key} id={group.slug} className="cz-catalog-group" data-reveal style={{ "--i": i } as React.CSSProperties}>
                    <Link to={groupPath(group)} className="cz-catalog-group__head">
                      <span className="cz-catalog-group__icon" aria-hidden="true">
                        <Icon size={24} strokeWidth={1.7} />
                      </span>
                      <span className="cz-catalog-group__text">
                        <strong className="cz-catalog-group__title">{groupTitle(group)}</strong>
                        <span className="cz-catalog-group__tagline">{group.tagline}</span>
                      </span>
                      <ArrowRight size={18} className="cz-catalog-group__arrow" aria-hidden="true" />
                    </Link>
                    <ul className="cz-catalog-group__items">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                          <li key={item.key}>
                            <Link to={itemPath(group, item)} className="cz-catalog-group__item">
                              <ItemIcon size={17} strokeWidth={1.8} aria-hidden="true" />
                              <span>{itemTitle(group, item)}</span>
                              <ArrowRight size={15} className="cz-catalog-group__item-arrow" aria-hidden="true" />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <CtaBand />
        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
